type ScrollAxis = 'x' | 'y';

const getElement = (target: EventTarget | null): HTMLElement | null => {
  if (target instanceof HTMLElement) {
    return target;
  }

  if (target instanceof Node) {
    return target.parentElement;
  }

  return null;
};

const canScroll = (element: HTMLElement, delta: number, axis: ScrollAxis, style: CSSStyleDeclaration): boolean => {
  const maxScroll =
    axis === 'y' ? element.scrollHeight - element.clientHeight : element.scrollWidth - element.clientWidth;
  const position = axis === 'y' ? element.scrollTop : element.scrollLeft + (style.direction === 'rtl' ? maxScroll : 0);

  if (maxScroll <= 1) {
    return false;
  }

  if (delta < 0) {
    return position > 0;
  }

  if (delta > 0) {
    return position < maxScroll - 1;
  }

  return true;
};

const canScrollWithinPage = (target: EventTarget | null, delta: number, axis: ScrollAxis): boolean => {
  let element = getElement(target);

  while (element && element !== document.body && element !== document.documentElement) {
    const style = window.getComputedStyle(element);
    const overflow = axis === 'y' ? style.overflowY : style.overflowX;

    if (overflow === 'auto' || overflow === 'scroll') {
      if (canScroll(element, delta, axis, style)) {
        return true;
      }

      const overscrollBehavior = axis === 'y' ? style.overscrollBehaviorY : style.overscrollBehaviorX;

      if (overscrollBehavior === 'contain' || overscrollBehavior === 'none') {
        return false;
      }
    }

    element = element.parentElement;
  }

  return false;
};

const preventIfNeeded = (event: Event, target: EventTarget | null, deltaX: number, deltaY: number): void => {
  if (!document.documentElement.hasAttribute('data-keyboard-open') || (deltaX === 0 && deltaY === 0)) {
    return;
  }

  const axis: ScrollAxis = Math.abs(deltaY) >= Math.abs(deltaX) ? 'y' : 'x';
  const delta = axis === 'y' ? deltaY : deltaX;

  if (event.cancelable && !canScrollWithinPage(target, delta, axis)) {
    event.preventDefault();
  }
};

export const initIosPageScrollLock = (): (() => void) => {
  let previousTouch: Touch | null = null;

  const handleTouchStart = (event: TouchEvent) => {
    previousTouch = event.touches.length === 1 ? event.touches[0] : null;
  };

  const handleTouchMove = (event: TouchEvent) => {
    const touch = event.touches.length === 1 ? event.touches[0] : null;
    const previous = previousTouch;

    previousTouch = touch;

    if (!touch || !previous || touch.identifier !== previous.identifier) {
      return;
    }

    const deltaY = previous.clientY - touch.clientY;
    const deltaX = previous.clientX - touch.clientX;

    preventIfNeeded(event, event.target, deltaX, deltaY);
  };

  const handleWheel = (event: WheelEvent) => {
    if (!event.ctrlKey) {
      preventIfNeeded(event, event.target, event.deltaX, event.deltaY);
    }
  };

  document.addEventListener('touchstart', handleTouchStart, { passive: true });
  document.addEventListener('touchmove', handleTouchMove, { passive: false });
  document.addEventListener('wheel', handleWheel, { passive: false });

  return () => {
    document.removeEventListener('touchstart', handleTouchStart);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('wheel', handleWheel);
  };
};
